import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useOrderTimeline } from '../../hooks/queries';
import {
  formatOrderBackLabel,
  resolveOrderDetailNavigationState,
  resolveOrderTimelineBack,
} from '../../utils/order-navigation';

interface TimelineEvent {
  type: string;
  timestamp: string;
  title: string;
  description: string | null;
  details?: {
    previousStatus?: string;
    newStatus?: string;
    taxTotal?: number;
    taxProvider?: string | null;
    fraudCheck?: string;
    source?: string;
  };
}

function formatEventDescription(event: TimelineEvent): string | null {
  if (event.description) {
    return event.description;
  }

  if (event.type === 'order_created' && event.details?.newStatus) {
    return `Status: ${event.details.newStatus}`;
  }

  if (event.type === 'order_created') {
    const status = (event.details as { status?: string } | undefined)?.status;
    return status ? `Status: ${status}` : null;
  }

  if (event.type === 'sales_tax_charged' && event.details?.taxProvider) {
    const total = event.details.taxTotal;
    const taxLine =
      total !== undefined ? `Tax Total: $${Number(total).toFixed(2)}` : null;
    return [taxLine, `Tax Provider: ${event.details.taxProvider}`]
      .filter(Boolean)
      .join(' | ');
  }

  if (event.type === 'payment_capture_success' && event.details) {
    const lines = [];
    if (event.details.fraudCheck) {
      lines.push(`Fraud Check: ${event.details.fraudCheck}`);
    }
    if (event.details.source) {
      lines.push(`Source: ${event.details.source}`);
    }
    return lines.length > 0 ? lines.join(' | ') : event.description;
  }

  return null;
}

export function OrderTimeline() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const orderId = parseInt(id ?? '0', 10);
  const backNav = resolveOrderTimelineBack(location.state, orderId);

  const { data: timelineResponse, isLoading: loading, error: queryError } = useOrderTimeline(
    orderId,
  );
  const timeline = timelineResponse?.data ?? null;
  const error = queryError ? (queryError as Error).message ?? 'Failed to fetch timeline' : null;

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleBack = (): void => {
    navigate(backNav.path, { state: resolveOrderDetailNavigationState(location.state) });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <div className="spinner" /> Loading timeline…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-banner">⚠ {error}</div>
        <button className="btn btn-secondary" onClick={handleBack}>
          {formatOrderBackLabel(backNav.label)}
        </button>
      </div>
    );
  }

  return (
    <div className="page-container order-timeline-page">
      <div className="order-timeline-header">
        <button
          className="order-timeline-back"
          onClick={handleBack}
        >
          {formatOrderBackLabel(backNav.label)}
        </button>
        <h1 className="order-timeline-title">Order timeline — Order {id}</h1>
      </div>

      {timeline && timeline.events.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-title">No events available for this order</p>
        </div>
      ) : timeline ? (
        <div className="order-timeline-list">
          {timeline.events.map((event: TimelineEvent, index: number) => (
            <div key={`${event.type}-${event.timestamp}-${index}`} className="order-timeline-event">
              <div className="order-timeline-time">{formatTimestamp(event.timestamp)}</div>
              <div className="order-timeline-axis">
                <span className="order-timeline-node" aria-hidden="true" />
              </div>
              <div className="order-timeline-card">
                <h3 className="order-timeline-card-title">{event.title}</h3>
                {formatEventDescription(event) && (
                  <p className="order-timeline-card-desc">{formatEventDescription(event)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
